/* eslint-disable no-undef */
import router from '@/router';
import auth from '@/Services/auth';
import { getError } from '@/utils/helpers';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import NProgress from "nprogress";

export const useAuthStore = defineStore('auth', () => {
  const authUser = ref(null),
   guest = ref(true),
   loading = ref(false),
   error = ref(null),
   dashboardUrl = () => {
      return authUser.value?.attributes?.isAdmin
          ? process.env.VUE_APP_ADMIN_DASHBOARD_URL
           : process.env.VUE_APP_USER_DASHBOARD_URL;
   },
   profileUrl = () => {
       return authUser.value?.attributes?.isAdmin
           ? process.env.VUE_APP_ADMIN_PROFILE_URL
           : process.env.VUE_APP_USER_PROFILE_URL;
   },

  /**
   * Sets the authenticated user. If the value is null, the user is considered a guest.
   * @param {Object|null} value The authenticated user or null if not authenticated.
   */
   setAuthUser = async (value) => {
    authUser.value = value;
  },

  /**
   * Sets the guest state.
   * @param {boolean} value The guest state.
   */
   setGuest = async (value) => {
    guest.value = value;
  },

  /**
   * Retrieves the current authenticated user.
   *
   * @returns {Object|null} The authenticated user object if logged in; otherwise, null.
   */
   getAuthUser = async () => {
    try {
      loading.value = true;
      const { data } = await auth.user();
      await setAuthUser(data.data);
      await setGuest(false);
      error.value = null;
    } catch (err) {
      error.value = getError(err);
    } finally {
      loading.value = false;
    }
  },

  /**
   * Attempts to log in the user with the provided credentials.
   * Sets the loading state to true while the login request is in progress.
   * If the login is successful, retrieves the authenticated user's details
   * and redirects the user to the appropriate dashboard based on their role.
   * If the user is an admin, they are redirected to the admin dashboard URL,
   * otherwise to the user dashboard URL. Any errors encountered are handled
   * by updating the error state. Regardless of success or failure, the loading
   * state is reset to false.
   *
   * @param {Object} payload - The login details, containing the user's email
   * and password.
   */
   attemptLogin = async (payload) => {
    try {
      loading.value = true;
      await auth.login(payload);
      await getAuthUser();

      if (router.currentRoute.value.name === 'Login') {
        if (authUser.value?.attributes?.isAdmin) {
          window.location  = process.env.VUE_APP_ADMIN_DASHBOARD_URL;
        } else {
          window.location = process.env.VUE_APP_USER_DASHBOARD_URL;
        }
        await router.push('/');
      }
      error.value = null;
    } catch (err) {
      return error.value = getError(err);
    } finally {
      NProgress.done();
      loading.value = false;
    }
  },

  /**
   * Logs out the current authenticated user.
   * Sets the loading state to true while the logout request is in progress
   * and handles any errors by updating the error state.
   * Upon successful logout, the authenticated user is set to null
   * and the guest state is set to true. Regardless of success or failure,
   * the loading state is reset to false.
   */
   logout = async () => {
    try {
      loading.value = true;
      if (getAuthUser() && authUser.value) {
          await auth.logout()
      }
        await setGuest(true);
      error.value = null;
      await router.push('/');
    } catch (err) {
      error.value = getError(err);
    } finally {
      loading.value = false;
    }
  },

  /**
   * Registers a new user and logs them in. The loading state is set to
   * true while the request is in progress and any errors are handled
   * by updating the error state. On successful registration and login,
   * the error state is reset to null. Regardless of success or failure,
   * the loading state is reset to false.
   *
   * @param {Object} payload - The registration details, containing the
   * user's email, password and password confirmation.
   */
   register = async (payload) => {
    try {
      loading.value = true;
      await auth.register(payload);
      error.value = null;
      await router.push('/login');
    } catch (err) {
      error.value = getError(err);
    } finally {
      NProgress.done();
      loading.value = false;
    }
  };

  return {
    authUser,
    guest,
    dashboardUrl,
    profileUrl,
    setAuthUser,
    setGuest,
    getAuthUser,
    attemptLogin,
    logout,
    register,
    loading,
    error,
  };
});
